/**
 * Frontend subscription service types and interfaces.
 *
 * This module defines the interfaces for the subscription/entitlement system
 * that manages access to premium features through Stripe integration.
 *
 * IMPORTANT: Entitlements control access to premium/paid features,
 * completely separate from the role-based administrative permission system.
 */

import type { CheckoutConflictReason } from '../../lib/checkoutConflict';

/** Live Stripe billing information from GET /v1/billing/status. */
export interface BillingStatus {
  has_subscription: boolean;
  subscription_status:
    | 'active'
    | 'trialing'
    | 'past_due'
    | 'unpaid'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | 'paused'
    | null;
  plan_name: string | null;
  amount: string | null;
  currency: string | null;
  next_billing_date: string | null;
  trial_end: string | null;
}

/**
 * Represents a single entitlement (premium feature access).
 * Frontend version with Date objects instead of Firestore Timestamps.
 */
export interface Entitlement {
  /** Unique identifier for the entitlement (e.g., 'premium', 'pro') */
  id: string;

  /** Whether this entitlement is currently active */
  isActive: boolean;

  /** Human-readable display name (enriched from backend catalog) */
  name?: string;

  /** Description of what this entitlement grants (enriched from backend catalog) */
  description?: string;

  /** How this entitlement was granted (enriched from backend catalog) */
  sources?: string[];

  /**
   * Whether an ACTIVE Stripe-provider subscription record backs this
   * entitlement. `sources` is provider-agnostic (a supapass grant also yields
   * 'subscription'), so use this flag to gate Stripe-only UI (e.g. the billing
   * portal "Manage" card). Active-only: a cancelled/expired Stripe sub does not
   * set it, even if the entitlement stays active via another source.
   */
  hasStripeSubscription?: boolean;

  /**
   * Whether an ACTIVE subscription record whose `provider` is exactly
   * `'supapass'` backs this entitlement (#2463). Compared exactly: `redeem`
   * records, manual grants and any other provider value leave it false.
   */
  hasSupapassSubscription?: boolean;

  /** Whether the entitlement will auto-renew */
  willRenew: boolean;

  /**
   * When access through this entitlement ends, MERGED across every active
   * source (Stripe, other providers, manual grants), so it is not a billing
   * date. Null when the API's `expiresAt` is null: a non-expiring source, or
   * one with no known end (#2463).
   */
  periodEndDate: Date | null;

  /**
   * The Stripe subscription record's own period end: when Stripe next bills,
   * or when a cancelled subscription ends. Absent when no Stripe record backs
   * this entitlement, or the record carries no date. Use this, not
   * `periodEndDate`, beside anything that reads as a billing date.
   */
  stripePeriodEndDate?: Date;

  /** Original purchase date */
  purchaseDate?: Date;

  /** Product identifier from the app store */
  productIdentifier?: string;
}

/**
 * Collection of all entitlements for a user.
 * Maps entitlement IDs to their current status.
 */
export type UserEntitlements = Record<string, Entitlement>;

/**
 * Subscription data for frontend use.
 */
export interface UserSubscription {
  /** User ID (matches authentication UID) */
  uid: string;

  /** Current entitlements (optional - may be fetched separately via getActiveEntitlements) */
  entitlements?: UserEntitlements;

  /** Last update timestamp (optional - may not be available from all endpoints) */
  updatedAt?: Date;

  /** Original subscription timestamp */
  createdAt?: Date;
}

/**
 * Service interface for checking user subscriptions in the frontend.
 *
 * @example
 * ```tsx
 * // In a React component
 * const subscriptionService = useSubscriptionService();
 * const hasPremium = await subscriptionService.hasEntitlement('premium');
 *
 * if (hasPremium) {
 *   return <PremiumFeature />;
 * } else {
 *   return <UpgradePrompt />;
 * }
 * ```
 */
export interface ISubscriptionService {
  /**
   * Check if the current user has a specific entitlement.
   *
   * @param entitlementId - The entitlement to check (e.g., 'premium')
   * @returns True if the user has an active entitlement
   */
  hasEntitlement(entitlementId: string): Promise<boolean>;

  /**
   * Get all active entitlements for the current user.
   *
   * @returns Map of active entitlements
   */
  getActiveEntitlements(): Promise<UserEntitlements>;

  /**
   * Get the full subscription data for the current user.
   *
   * @returns The subscription data or null if not found
   */
  getSubscription(): Promise<UserSubscription | null>;

  /**
   * Get all subscriptions for the current user (plural endpoint).
   *
   * @returns Array of subscriptions (currently single subscription, foundation for multi-subscription)
   */
  getSubscriptions(): Promise<SubscriptionsResponse>;

  /**
   * Get the expiration date for a specific entitlement.
   *
   * @param entitlementId - The entitlement to check
   * @returns The expiration date or null if not active
   */
  getEntitlementExpiration(entitlementId: string): Promise<Date | null>;

  /**
   * Initiate a purchase flow for a specific product.
   * This will integrate with Stripe Checkout.
   *
   * @param productId - The product to purchase
   * @returns Purchase result or throws on error
   */
  purchaseProduct(productId: string): Promise<PurchaseResult>;

  /**
   * Restore previous purchases.
   * Useful when user signs in on a new device.
   */
  restorePurchases(): Promise<void>;

  /**
   * Get available products and pricing information.
   * This is a public endpoint that does not require authentication.
   *
   * @returns Product catalog with pricing for all available products
   */
  getProducts(): Promise<{
    products: Array<{
      productId: string;
      name: string;
      description: string;
      entitlement: string;
      prices: Array<{
        currency: string;
        amount: number;
        priceId: string;
        interval: string;
      }>;
      features?: string[];
      highlights?: string[];
    }>;
    detectedCurrency?: string;
  }>;
}

/**
 * Result of a purchase operation.
 */
export interface PurchaseResult {
  /** Whether the purchase was successful */
  success: boolean;

  /** The entitlement(s) granted by this purchase */
  entitlements?: string[];

  /** Error message if purchase failed */
  error?: string;

  /**
   * The backend's error category, when the failure was a refusal that has
   * one — e.g. `EMAIL_NOT_VERIFIED` (#2037). Lets a caller act on WHICH
   * refusal it was without matching on the message.
   */
  errorCode?: string;

  /**
   * Which case a checkout refused for an existing HeapsterLive subscription
   * is (#2218). Set only with `errorCode: HEAPSTERLIVE_SUBSCRIPTION_EXISTS`.
   */
  conflictReason?: CheckoutConflictReason;

  /** Whether this was a restored purchase */
  restored?: boolean;
}

/**
 * A failed purchase, as `usePurchase` throws it: the service's message, with
 * the backend's error category kept beside it rather than lost in a plain
 * `Error`.
 */
export class PurchaseError extends Error {
  readonly code?: string;
  readonly conflictReason?: CheckoutConflictReason;

  constructor(
    message: string,
    code?: string,
    conflictReason?: CheckoutConflictReason
  ) {
    super(message);
    this.name = 'PurchaseError';
    this.code = code;
    this.conflictReason = conflictReason;
  }
}

/**
 * Configuration for the subscription service.
 */
export interface SubscriptionConfig {
  /** Stripe public API key */
  publicApiKey: string;

  /** Available products for purchase */
  products: ProductInfo[];

  /** Whether to use sandbox environment */
  useSandbox: boolean;
}

/**
 * Information about a purchasable product.
 */
export interface ProductInfo {
  /** Product ID from app store */
  id: string;

  /** Display name for the product */
  name: string;

  /** Description of what's included */
  description: string;

  /** Price string (e.g., "$9.99/month") */
  priceString?: string;

  /** The entitlement(s) this product grants */
  entitlements: string[];

  /** Whether this is a subscription or one-time purchase */
  isSubscription: boolean;
}

/**
 * Stripe price information for a specific billing interval and currency.
 */
export interface StripePrice {
  /** Currency code (lowercase, e.g., 'usd', 'gbp') */
  currency: string;

  /** Price amount in smallest currency unit (cents, pence) */
  amount: number;

  /** Stripe Price ID for checkout */
  priceId: string;

  /** Billing interval (e.g., 'month', 'year') */
  interval: string;
}

/**
 * Stripe product with multi-currency pricing information.
 */
export interface StripeProduct {
  /** Stripe Product ID */
  productId: string;

  /** Product display name */
  name: string;

  /** Product description */
  description: string;

  /** Entitlement ID granted by this product */
  entitlement: string;

  /** Available prices for this product across currencies */
  prices: StripePrice[];

  /** Product features from metadata (optional) */
  features?: string[];

  /** Product highlights from metadata (optional) */
  highlights?: string[];
}

/**
 * Response from /v1/products endpoint with dynamic Stripe pricing.
 */
export interface StripePricing {
  /** Array of products with pricing information */
  products: StripeProduct[];

  /** Detected currency based on user's country (optional) */
  detectedCurrency?: string;
}

/**
 * Single subscription item from the plural subscriptions endpoint.
 */
export interface SubscriptionData {
  /** Unique subscription identifier */
  subscriptionId: string;

  /** Product identifier from Stripe */
  productId: string | null;

  /** Subscription status */
  status: 'active' | 'inactive';

  /** When the subscription expires (ISO 8601) */
  expiresAt: string | null;

  /** Whether subscription will automatically renew */
  willRenew: boolean;

  /** Sources contributing to this entitlement ('subscription', 'manual', or both) */
  sources: string[];
}

/**
 * Response from GET /v1/users/{uid}/subscriptions endpoint.
 */
export interface SubscriptionsResponse {
  /** Array of user subscriptions */
  subscriptions: SubscriptionData[];
}

/**
 * Hook return type for subscription status.
 */
export interface SubscriptionStatus {
  /** Current subscription data */
  subscription: UserSubscription | null;

  /** Whether subscription data is loading */
  isLoading: boolean;

  /** Any error that occurred */
  error: Error | null;

  /** Helper to check specific entitlement */
  hasEntitlement: (entitlementId: string) => boolean;

  /** Refetch subscription data */
  refetch: () => Promise<void>;
}
